import React from 'react';
import update from 'immutability-helper';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { DndProvider } from 'react-dnd';
import { Button, Card } from '@avila-tek/ui/src';
import { useMutation } from '@apollo/client';
import NProgress from 'nprogress';
import { Category } from '../../models';
import { UPDATE_CATEGORIES_POSITIONS } from '../../graphql/mutation';
import { useNotify } from '../../hooks';
import DraggableCategory from './DraggableCategory';

interface CategoriesPositionProps {
  categories: Category[];
}

function CategoriesPosition({ categories }: CategoriesPositionProps) {
  const notify = useNotify();

  const [disable, setDisable] = React.useState(false);

  const [itemsIds, setItemsIds] = React.useState(() =>
    categories?.map((category) => category?._id)
  );

  const [updateCategoriesPositions] = useMutation<{
    updateCategoriesPositions: {
      success: boolean;
    };
  }>(UPDATE_CATEGORIES_POSITIONS);

  const updateItems = React.useCallback((idx: number, data: string) => {
    setItemsIds((prev) => [
      ...prev.slice(0, idx),
      data,
      ...prev.slice(idx + 1),
    ]);
  }, []);

  const moveCard = React.useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setItemsIds((prev) =>
        update(prev, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, prev[dragIndex]],
          ],
        })
      );
    },
    []
  );

  const getBackend = () => {
    if (process.browser && 'ontouchstart' in window) {
      return TouchBackend;
    }
    return HTML5Backend;
  };

  const handleUpdate = async () => {
    try {
      if (disable) return;
      setDisable(true);
      NProgress.start();
      const { data } = await updateCategoriesPositions({
        variables: {
          data: {
            positions: itemsIds.map((itemId, idx) => ({
              _id: itemId,
              position: idx,
            })),
          },
        },
      });

      if (data.updateCategoriesPositions.success) {
        notify('Se ha actualizado la posición de las categorías', 'success');
      } else {
        notify(
          'No se ha podido actualizar la posición de las categorías',
          'error'
        );
      }
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setDisable(false);
      NProgress.done();
    }
  };

  return (
    <div className="flex flex-col">
      <Card>
        <Card.Header>Posición de las categorías</Card.Header>
        <Card.Body>
          <div className="w-full flex flex-col gap-2 max-h-[50vh] pr-2 overflow-auto scrollbar-thin scrollbar-track-cream scrollbar-thumb-pink-300">
            <DndProvider backend={getBackend()}>
              {itemsIds?.length > 0 ? (
                itemsIds?.map((itemId, idx) => (
                  <DraggableCategory
                    idx={idx}
                    category={categories?.find((cat) => cat?._id === itemId)}
                    updateItems={updateItems}
                    moveCard={moveCard}
                    selectedItemIds={itemsIds}
                    setSelectedItemIds={setItemsIds}
                  />
                ))
              ) : (
                <p className="font-display text-xsd text-center my-5 py-2 px-5 border border-gray-100 rounded-full">
                  No hay productos agregados a esta colección
                </p>
              )}
            </DndProvider>
          </div>
        </Card.Body>
      </Card>
      <div className="ml-auto mt-2">
        <Button onClick={() => handleUpdate()}>Guardar</Button>
      </div>
    </div>
  );
}

export default CategoriesPosition;
